<template>
    <Transition 
        :duration="transitionProps.duration" 
        :name="transitionProps.name"
    >
        <div 
            v-if="isOpen"
            component-name="modal" 
            class="overflow-hidden fixed z-50 inset-0 w-full h-full" 
            @keyup.esc="handleCloseRequest"
        >
            <span v-if="useBackdrop" data-target="backdrop" class="fixed inset-0 w-full h-full bg-black/50" aria-hidden="true"></span>

            <div 
                data-target="content" 
                ref="tpl_content"
                class="relative top-0 left-0 flex flex-col w-full h-full overflow-y-auto scrolltrigger-container" 
                :class="{'bg-skin-base': useSolidBackground}"
            >
                <header v-if="useClose" class="sticky z-[2] top-0 left-0 w-full grow-0 bg-skin-base">
                    <btn-secondary @click="handleCloseRequest" class="uppercase w-full !border-t-0 !border-x-0" :title="$t.close" />
                </header>

                <div class="w-full grow">
                    <slot :wrapper="tpl_content"/>
                </div>
            </div>
        </div>
    </Transition>
</template>

<script setup>
const { $resumeScrollSmoother, $pauseScrollSmoother } = useNuxtApp();
const props = defineProps({
  isOpen: {
    type: Boolean,
    required: false,
    default: false,
  },
  transition: {
    type: String,
    required: false,
    validator: function (value) {
      return (
        ["none", "slide-from-bottom", "slide-from-left"].indexOf(value) !== -1
      );
    },
    default: "none",
  },
  useClose: {
    type: Boolean,
    required: false,
    default: true,
  },
  useBackdrop: {
    type: Boolean,
    required: false,
    default: true,
  },
  useSolidBackground: {
    type: Boolean,
    required: false,
    default: true,
  },
});
const emit = defineEmits(["open", "close"]);
const tpl_content = templateRef("tpl_content");

const transitionProps = computed(() => {
  let obj = {};

  switch (props.transition) {
    case "slide-from-bottom":
      obj.name = "modal-slide-from-bottom";
      obj.duration = 700;
      break;

    case "slide-from-left":
      obj.name = "modal-slide-from-left";
      obj.duration = 700;
      break;

    case "none":
    default:
      obj.name = "";
      obj.duration = 0;
      break;
  }

  return obj;
});

function handleCloseRequest() {
  emit("close");
}

function handleOpenRequest() {
  emit("open");
}

watch(
  () => props.isOpen,
  (newVal, oldVal) => {
    newVal ? $pauseScrollSmoother() : $resumeScrollSmoother();
  }
);

onMounted(() => {
  if (props.isOpen) $pauseScrollSmoother();
});
</script>

<style scoped>
/* BACKDROP TRANSITION */
.modal-slide-from-bottom-enter-active span[data-target="backdrop"],
.modal-slide-from-left-enter-active span[data-target="backdrop"] {
  @apply transition-opacity duration-500 ease-out;
}

.modal-slide-from-bottom-enter-from span[data-target="backdrop"],
.modal-slide-from-bottom-left-from span[data-target="backdrop"] {
  @apply opacity-0;
}

.modal-slide-from-bottom-enter-to span[data-target="backdrop"],
.modal-slide-from-bottom-left-to span[data-target="backdrop"] {
  @apply opacity-100;
}

.modal-slide-from-bottom-leave-active span[data-target="backdrop"],
.modal-slide-from-left-leave-active span[data-target="backdrop"] {
  @apply transition-opacity delay-200 duration-500 ease-in-out;
}

.modal-slide-from-bottom-leave-from span[data-target="backdrop"],
.modal-slide-from-left-leave-from span[data-target="backdrop"] {
  @apply opacity-100;
}

.modal-slide-from-bottom-leave-to span[data-target="backdrop"],
.modal-slide-from-left-leave-to span[data-target="backdrop"] {
  @apply opacity-0;
}
/* end BACKDROP TRANSITION */

/* CONTENT TRANSITION */
.modal-slide-from-bottom-enter-active div[data-target="content"],
.modal-slide-from-left-enter-active div[data-target="content"] {
  @apply transition-transform delay-200 duration-500 ease-in-out;
}

.modal-slide-from-bottom-enter-from div[data-target="content"] {
  @apply translate-y-full;
}

.modal-slide-from-left-enter-from div[data-target="content"] {
  @apply -translate-x-full;
}

.modal-slide-from-bottom-enter-to div[data-target="content"] {
  @apply translate-y-0;
}

.modal-slide-from-left-enter-to div[data-target="content"] {
  @apply translate-x-0;
}

.modal-slide-from-bottom-leave-active div[data-target="content"],
.modal-slide-from-left-leave-active div[data-target="content"] {
  @apply transition-transform duration-500 ease-in-out;
}

.modal-slide-from-bottom-leave-from div[data-target="content"] {
  @apply translate-y-0;
}

.modal-slide-from-left-leave-from div[data-target="content"] {
  @apply translate-x-0;
}

.modal-slide-from-bottom-leave-to div[data-target="content"] {
  @apply translate-y-full;
}

.modal-slide-from-left-leave-to div[data-target="content"] {
  @apply -translate-x-full;
}
/* end CONTENT TRANSITION */
</style>